import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Select, Table, Tag, Button, Typography, Space } from "antd";
import { getDevices, getUpdateLogs } from "../../store";
import useIsMobile from "../../hooks/useIsMobile";

const LEVEL_COLOR = { error: "red", warn: "orange", warning: "orange", info: "blue" };

const fmtTime = (v) => (v || "").replace("T", " ").slice(0, 19);

const columns = [
  { title: "Received (UTC)", dataIndex: "received_at", key: "received_at", width: 170, render: fmtTime },
  { title: "Phase", dataIndex: "phase", key: "phase", width: 110 },
  {
    title: "Level", dataIndex: "level", key: "level", width: 90,
    render: (v) => <Tag color={LEVEL_COLOR[v] || "default"}>{v || "-"}</Tag>,
  },
  { title: "Event", dataIndex: "event", key: "event", width: 150 },
  {
    title: "Firmware", key: "fw", width: 130,
    render: (_, r) => (r.fw_version || r.fw_target) ? `${r.fw_version || "?"} → ${r.fw_target || "?"}` : "",
  },
  { title: "Message", dataIndex: "message", key: "message" },
];

// Admin-only page: RAD OTA boot/update events for a device, for debugging boot problems.
export const UpdateLogs = () => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [devices, setDevices] = useState([]);
  const [serial, setSerial] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(getDevices()).unwrap()
      .then((data) => setDevices((data || []).map((d) => d && d.serial_number).filter(Boolean)))
      .catch(() => setDevices([]));
  }, [dispatch]);

  const load = useCallback((s) => {
    if (!s) return;
    setLoading(true);
    dispatch(getUpdateLogs({ serial: s, limit: 300 })).unwrap()
      .then((data) => setEvents((data && data.events) || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [dispatch]);

  const onSelect = (s) => { setSerial(s); load(s); };

  return (
    <div style={{ padding: isMobile ? 14 : 24 }}>
      <Typography.Title level={3}>Device Update Logs</Typography.Title>
      <Typography.Paragraph type="secondary">
        RAD OTA boot/update events (newest first) for debugging boot problems. Failures are highlighted.
      </Typography.Paragraph>
      <Space style={{ marginBottom: 16, width: isMobile ? "100%" : undefined }} wrap>
        <Select
          // On mobile, tap-to-open (no search input) so iOS doesn't pop the keyboard +
          // password-autofill bar over the device list; desktop keeps type-ahead search.
          showSearch={!isMobile}
          allowClear
          placeholder="Select a device"
          style={{ width: isMobile ? "100%" : 260, minWidth: 220 }}
          value={serial}
          onChange={onSelect}
          optionFilterProp="label"
          options={devices.map((s) => ({ value: s, label: s }))}
        />
        <Button onClick={() => load(serial)} disabled={!serial} loading={loading}>Refresh</Button>
      </Space>
      <Table
        rowKey="event_time"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={events}
        scroll={{ x: "max-content" }}   // horizontal scroll inside the table instead of bleeding off-page
        pagination={{ pageSize: 50, showSizeChanger: false }}
        onRow={(r) => ({ style: r.is_failure ? { background: "#fff1f0" } : {} })}
        locale={{ emptyText: serial ? "No update events for this device" : "Select a device to view its update logs" }}
      />
    </div>
  );
};

export default UpdateLogs;
